// Ambient-Summary-Store: aktive Phase + Preset + Brightness pro
// ambient_group-Regel, dazu Szenen und Bewegungslicht-Regeln (bathroom_light).
// Liefert dem Dashboard-Tile den Status auf einen Blick und der
// /zuhause/ambient-Detail-Page die volle Sicht zur Steuerung.
//
// Live-Update (Realtime-Mandat) über das Homelab-Protokoll (docs/protokoll.md
// › „Licht über das Protokoll“, seit 02.11.2026): jede Regel ist eine Instanz
// (licht.bad.<id>, licht.ambient.<id>, licht.szene.<id>); der Name steht im
// Stand. Das Backend baut die Sicht alle 10 s und bei jedem Signal
// `automations:state` und setzt die Instanzen; der Hub verwirft Unverändertes
// und schickt Deltas. Bis dahin holte der Store alle 10 s und bei jedem Signal
// drei Listen neu (38 kB je Runde, 80 % davon Lernkurven, die sich selten
// ändern) -- je Tab.
//   - REST (`/v2-api/ambient`, `/ambient/scenes`, `/bathroom`) beim Start, als
//     Revalidator, in refreshAmbient (nach einer Aktion, deren Ergebnis die
//     Seite sofort zeigen soll) und als Neuversuch nach einem gescheiterten
//     Abruf. KEIN Takt, KEIN Abo auf `automations:state` mehr.
//   - `licht.<art>.*` (Rate normal): Snapshot + Delta je Regel. Ein Stand
//     aktualisiert die Regel in der Liste in-place (Objekt-Identität und
//     Reihenfolge bleiben), eine unbekannte hängt sich hinten an, `weg` nimmt
//     sie heraus. Der Instanzname ist ein Hash; die Regel findet der Store
//     über den Namen aus dem Stand (merkt sich Instanz -> Name, denn `weg`
//     kennt nur den Instanznamen). Der jüngste Stand je Regel wird mit
//     laufender Nummer gemerkt; kam er während eines REST-Abrufs, liegt er
//     über dessen Antwort (sie kann dahinter liegen), ältere nicht. Ein
//     `weg` hinterlässt einen Grabstein, damit eine Antwort, die die Regel
//     noch nennt, sie nicht zurückholt.
//
// Wire-Form mirrort internal/automations/ambient_view.go, bathroom_view.go und
// scene.go; aus dem Protokoll-Stand baut sie lib/licht-protokoll.ts.
//
// SWR: ja -- Dashboard-Tile + /zuhause/ambient zeigen beim Reload sofort die
// letzten Werte.

import { getJSON } from "./api";
import { ambientAusProtokoll, badAusProtokoll, lichtArtAusInstanz, szeneAusProtokoll, type LichtArt } from "./licht-protokoll";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { LichtAmbient, LichtBad, LichtSzene } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";
import { mitStaenden, uebernimm, type Gemerkt } from "./storage-protokoll";

export interface Preset {
  name: string;
  colors: string[];
}

export interface PhaseBoundary {
  sun?: "sunrise" | "sunset" | "";
  offset_min?: number;
  time?: string;
}

export interface AmbientPhaseView {
  label: string;
  presets: Preset[];
  active_preset: number;
  brightness: number;
  default_brightness: number;
  override: boolean;
  start: PhaseBoundary;
  start_hour: number;
}

export interface AmbientTarget {
  topic: string;
  type?: string;
}

export interface AmbientSun {
  sunrise_h: number;
  sunset_h: number;
  next_phase?: string;
  next_phase_at_unix?: number;
}

export interface AmbientView {
  name: string;
  enabled: boolean;
  on: boolean;
  current_phase: string; // "morning" | "day" | "evening" | "night"
  phase_label: string;
  phases: Record<string, AmbientPhaseView>;
  active_scene?: string;
  sun: AmbientSun;
  targets: AmbientTarget[];
  phase_transition_s: number;
  default_phase_transition_s: number;
  max_phase_transition_s: number;
  evening_end: number;
  adaptive: AdaptiveView;
}

export interface SceneTarget {
  topic: string;
  type?: string;
  payload: Record<string, unknown>;
}

export interface SceneView {
  name: string;
  enabled: boolean;
  active: boolean;
  on_go_engine: boolean;
  restore_ambient?: string;
  target_count: number;
  colors?: string[];
  targets: SceneTarget[];
}

export interface BathroomTriggers {
  motion_topic?: string;
  door_topic?: string;
  dimmer_topic?: string;
  switch_topics?: string[];
  motion_timeout: number;
  door_off_delay?: number;
}

export interface BathroomPhaseView {
  label: string;
  brightness: number;
  default_brightness: number;
  brightness_override: boolean;
  color_temp: number;
  default_color_temp: number;
  color_temp_override: boolean;
  transition?: number;
  start: PhaseBoundary;
  start_hour: number;
}

export interface AdaptivePoint {
  minute: number;
  brightness: number;
  confirm?: boolean; // weiche Bestaetigung -> eigenes Symbol (Raute)
}

export interface AdaptiveCurvePt {
  minute: number;
  brightness: number;
  confident: boolean;
  filled?: boolean;
  prov_brightness?: number; // vorlaeufiger Preview-Wert (noch nicht solide)
}

export interface AdaptiveSegment {
  start_min: number;
  end_min: number;
  brightness: number;
}

export interface AdaptiveView {
  enabled: boolean;
  track: boolean;
  sample_count: number;
  samples: AdaptivePoint[];
  curve: AdaptiveCurvePt[];
  segments: AdaptiveSegment[];
  now_brightness: number;
  now_confident: boolean;
  now_prov_brightness?: number; // vorlaeufiger Jetzt-Wert (Preview)
  pending?: AdaptivePoint | null; // noch nicht gelernter Wunsch (durchscheinend)
}

export interface BathroomView {
  name: string;
  enabled: boolean;
  on: boolean;
  manual_override: boolean;
  last_source?: string;
  light_topic: string;
  current_phase: string;
  phase_label: string;
  phases: Record<string, BathroomPhaseView>;
  sun: AmbientSun;
  triggers: BathroomTriggers;
  evening_end: number;
  adaptive: AdaptiveView;
}

const KEY_AMBIENT = "ambient";
const KEY_SCENES = "ambient-scenes";
const KEY_BATHROOM = "bathroom";

type Eintrag = AmbientView | SceneView | BathroomView;

// Tief reaktiv: Seiten lesen Felder, ein Stand ändert nur die betroffenen.
let _ambient = $state<AmbientView[]>([]);
let _scenes = $state<SceneView[]>([]);
let _bathroom = $state<BathroomView[]>([]);
let _started = false;
let _laedt = true;
let _unsub: Array<() => void> = [];

// Je Art: Topic-Familie (für Abo und skaliere), Persistenz-Schlüssel, Zugriff
// auf die Liste und die Umrechnung Stand -> REST-Form. Schlüssel eines
// Eintrags ist sein Name (die Regelnamen sind je Art eindeutig).
interface ArtInfo {
  familie: string;
  key: string;
  lesen: () => Eintrag[];
  setzen: (l: Eintrag[]) => void;
  aus: (daten: Record<string, unknown>) => Eintrag;
}

const ARTEN: Record<LichtArt, ArtInfo> = {
  bad: {
    familie: "licht.bad",
    key: KEY_BATHROOM,
    lesen: () => _bathroom,
    setzen: (l) => (_bathroom = l as BathroomView[]),
    aus: (d) => badAusProtokoll(skaliere<LichtBad>("licht.bad", d)),
  },
  ambient: {
    familie: "licht.ambient",
    key: KEY_AMBIENT,
    lesen: () => _ambient,
    setzen: (l) => (_ambient = l as AmbientView[]),
    aus: (d) => ambientAusProtokoll(skaliere<LichtAmbient>("licht.ambient", d)),
  },
  szene: {
    familie: "licht.szene",
    key: KEY_SCENES,
    lesen: () => _scenes,
    setzen: (l) => (_scenes = l as SceneView[]),
    aus: (d) => szeneAusProtokoll(skaliere<LichtSzene>("licht.szene", d)),
  },
};
const ALLE_ARTEN = Object.keys(ARTEN) as LichtArt[];

// Der jüngste Protokoll-Stand je Regel, mit laufender Nummer des Eintreffens:
// ein Abruf legt nur Stände über seine Antwort, die nach seinem Start kamen
// -- ältere hat der Server beim Bauen der Antwort schon gekannt (und nach
// einer Unterbrechung des Protokolls wären sie veraltet). `e: null` = die
// Regel ist weg: eine Antwort, die sie noch nennt, holt sie nicht zurück.
const _merk: Record<LichtArt, Map<string, Gemerkt<Eintrag>>> = {
  bad: new Map(),
  ambient: new Map(),
  szene: new Map(),
};
let _nr = 0;
// Instanzname -> Regelname: `weg` nennt nur die Instanz.
const _namen = new Map<string, string>();

// ── Persistenz ─────────────────────────────────────────────────────────

// Aus dem Protokoll kommt oft etwas (jede Runde ein paar Regeln, z. B. die
// Übergangszeit): gespeichert wird gebündelt, höchstens alle 2 s.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    for (const art of ALLE_ARTEN) persist(ARTEN[art].key, $state.snapshot(ARTEN[art].lesen()));
  }, 2_000);
}

// ── Stände ────────────────────────────────────────────────────────────

function beiStand(art: LichtArt, instanz: string, daten: Record<string, unknown>): void {
  const info = ARTEN[art];
  const neu = info.aus(daten);
  const k = neu.name;
  _namen.set(instanz, k);
  _merk[art].set(k, { e: neu, nr: ++_nr });
  const l = info.lesen();
  const ex = l.find((x) => x.name === k);
  if (ex) {
    if (uebernimm(ex, neu)) persistSpaeter();
  } else if (!_laedt) {
    // Eine Regel, die die Liste noch nicht kennt (eben dazugekommen). Vor dem
    // ersten Abruf legt laden() den Stand über die Antwort.
    l.push(neu);
    persistSpaeter();
  }
}

function beiWeg(art: LichtArt, instanz: string): void {
  const k = _namen.get(instanz);
  if (k === undefined) return;
  _namen.delete(instanz);
  _merk[art].set(k, { e: null, nr: ++_nr });
  const l = ARTEN[art].lesen();
  const i = l.findIndex((x) => x.name === k);
  if (i >= 0) {
    l.splice(i, 1);
    persistSpaeter();
  }
}

// ── REST ───────────────────────────────────────────────────────────────

type Antwort = Record<LichtArt, Eintrag[]>;

// Gab es seit `ab` etwas aus dem Protokoll?
function neuereStaende(ab: number): boolean {
  return ALLE_ARTEN.some((a) => [..._merk[a].values()].some((g) => g.nr > ab));
}

// anwenden merged die frische Antwort IN-PLACE in die bestehenden Listen:
// vorhandene Regeln behalten Objekt-Identität (nur Felder werden
// aktualisiert), die Reihenfolge der Antwort gilt, Neue hängen sich hinten
// an. So kein Voll-Re-Render bei jedem Wert. `rest` fehlt, wenn der Abruf
// scheiterte: dann gilt, was da ist, plus was das Protokoll währenddessen
// brachte.
function anwenden(rest: Antwort | undefined, ab: number): void {
  if (!rest && !neuereStaende(ab)) return;
  for (const art of ALLE_ARTEN) {
    const info = ARTEN[art];
    const cur = info.lesen();
    const eingang = rest ? rest[art] : cur;
    const alle = mitStaenden(eingang, _merk[art], ab, (e) => e.name);
    const byName = new Map(cur.map((e) => [e.name, e]));
    const next = alle.map((e) => {
      const ex = byName.get(e.name);
      if (ex) {
        uebernimm(ex, e);
        return ex;
      }
      return e;
    });
    const same = next.length === cur.length && next.every((e, i) => e === cur[i]);
    if (!same) info.setzen(next);
  }
  persistSpaeter();
}

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt käme sonst
// erst der nächste Revalidator -- und bis dahin bliebe eine aus der Ablage
// geholte, inzwischen gelöschte Regel stehen (für sie kommt kein `weg`,
// diese Sitzung kannte ihre Instanz nie).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;

// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach stopAmbient
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

async function laden(): Promise<void> {
  const ab = _nr;
  const lauf = _lauf;
  try {
    const [ambient, szene, bad] = await Promise.all([
      getJSON<AmbientView[]>("/v2-api/ambient"),
      getJSON<SceneView[]>("/v2-api/ambient/scenes"),
      getJSON<BathroomView[]>("/v2-api/bathroom"),
    ]);
    if (lauf !== _lauf) return;
    anwenden({ ambient, szene, bad }, ab);
  } catch {
    if (lauf !== _lauf) return;
    // Was das Protokoll während des Abrufs gebracht hat, gilt trotzdem --
    // beim ersten Abruf wären die Listen sonst leer, bis jede Regel sich
    // ändert.
    anwenden(undefined, ab);
    if (_started && !_neuversuch) {
      _neuversuch = setTimeout(() => {
        _neuversuch = undefined;
        if (_started) void laden();
      }, NEUVERSUCH_MS);
    }
  } finally {
    if (lauf === _lauf) _laedt = false;
  }
}

export function startAmbient(): void {
  if (_started) return;
  _started = true;
  _lauf++;
  _laedt = true;
  // SWR: gecachte Ambient/Scenes/Bathroom-Views einspielen, damit das
  // Dashboard-Tile + /zuhause/ambient beim Reload sofort die letzten Werte
  // zeigt.
  const cAmbient = loadPersisted<AmbientView[]>(KEY_AMBIENT);
  if (cAmbient) _ambient = cAmbient;
  const cScenes = loadPersisted<SceneView[]>(KEY_SCENES);
  if (cScenes) _scenes = cScenes;
  const cBath = loadPersisted<BathroomView[]>(KEY_BATHROOM);
  if (cBath) _bathroom = cBath;
  void laden();
  for (const art of ALLE_ARTEN) {
    _unsub.push(
      abonniere(
        `${ARTEN[art].familie}.*`,
        { rate: "normal" },
        {
          stand: (instanz, daten) => {
            if (lichtArtAusInstanz(instanz)?.art === art) beiStand(art, instanz, daten);
          },
          weg: (instanz) => {
            if (lichtArtAusInstanz(instanz)?.art === art) beiWeg(art, instanz);
          },
        },
      ),
    );
  }
  // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reißt die Leitung ab -- und
  // jeder Deploy tut das --, sind verpasste Löschungen weg. registerRevalidator
  // holt beim Reconnect und beim Zurückkommen des Tabs alles frisch. (Der
  // Protokoll-Client holt sich nach einer Lücke selbst einen Snapshot je
  // Instanz; die Revalidation deckt zusätzlich Regeln ab, die während der
  // Lücke verschwanden.)
  _unsub.push(registerRevalidator(laden));
}

export function stopAmbient(): void {
  _started = false;
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _ambient = [];
  _scenes = [];
  _bathroom = [];
  for (const a of ALLE_ARTEN) _merk[a].clear();
  _namen.clear();
}

export function getAmbient(): AmbientView[] {
  return _ambient;
}

export function getScenes(): SceneView[] {
  return _scenes;
}

export function getBathroom(): BathroomView[] {
  return _bathroom;
}

export function refreshAmbient(): Promise<void> {
  return laden();
}
